import classes from '@auth/common/common.module.scss';
import { NormalText } from '@auth/common/styles';
import { getApiErrorMessage } from '@components/common/util/util';
import { analytics } from '@lib/analytics';
import { BI_EVENTS, CATEGORY, TRACK_TYPE } from '@lib/analyticsEvents';
import auth from '@services/cognito';
import Button from '@ui-library/button';
import Input from '@ui-library/input';
import ErrorLabel from '@ui-library/errorLabel';
import { Flex } from '@ui-library/misc';
import { Label } from '@ui-library/typography';
import validateEmail from '@utils/validateEmail';
import React, { useState } from 'react';
import { OPENSOURCE, SIGNUP_ERROR_MESSAGES } from '../../common/constants';
import { checkIfWorkEmail } from '../../common/util';

interface IProps {
  type: string | null;
  onEmailSent: () => void;
  email: string;
  updateEmail: (val: string) => void;
}

const SignupWithEmail = ({ type, onEmailSent, email, updateEmail }: IProps) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const isValidEmail = (e: string, t: string | null) => {
    if (e === '') {
      setError(true);
      setErrorMessage(SIGNUP_ERROR_MESSAGES.MISSING_FIELDS);
      return;
    }

    if (!validateEmail(e)) {
      setError(true);
      setErrorMessage(SIGNUP_ERROR_MESSAGES.INVALID_MAIL);
      return;
    }

    if (t !== OPENSOURCE && !checkIfWorkEmail(e)) {
      setError(true);
      setErrorMessage(SIGNUP_ERROR_MESSAGES.INVALID_WORK_MAIL);
      return;
    }

    setError(false);
  };

  const handleEmailUpdate = (input: React.FormEvent<HTMLInputElement>) => {
    input.preventDefault();
    updateEmail(input.currentTarget.value.trim().toLowerCase());
    isValidEmail(input.currentTarget.value.trim().toLowerCase(), type);
  };

  const handleSignup = async () => {
    setLoading(true);
    setError(false);
    setErrorMessage('');
    try {
      await auth.signupMail(email, type !== OPENSOURCE);
      onEmailSent();
      logSignup();
    } catch (err) {
      setLoading(false);
      setError(true);
      setErrorMessage(getApiErrorMessage(err, 'Failed to signup'));
      logSignup(getApiErrorMessage(err, 'Failed to signup'), true);
    }
  };

  const logSignup = (message?: string, isError = false) => {
    analytics.track(BI_EVENTS.SIGN_UP_WITH_EMAIL, TRACK_TYPE.CLICK, CATEGORY.AUTH, {
      email,
      type,
      isError,
      errorMessage: message,
    });
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLElement>) => {
    if (!error && email.length > 0 && event.key === 'Enter') {
      handleSignup();
    }
  };

  return (
    <>
      <div style={{ marginBottom: '40px' }}>
        <Label fontWeight={700} fontSize="24px" lineHeight="36px" color="#101836">
          {type === OPENSOURCE ? 'Sign up for RudderStack Open Source' : 'Signup for RudderStack'}
        </Label>
      </div>
      <Flex flexDirection="column" style={{ marginBottom: '32px' }}>
        <div style={{ marginBottom: '6px' }}>
          <Label fontWeight={400} fontSize="14px" lineHeight="22px" color="#101836">
            Email
          </Label>
        </div>
        <Input
          data-testid="email"
          id="text-input-email"
          type="email"
          value={email}
          className="m-b-md"
          maxLength={64}
          placeholder={type === OPENSOURCE ? 'name@email.com' : 'name@workemail.com'}
          onKeyDown={handleKeyPress}
          onChange={(e: React.FormEvent<HTMLInputElement>) => handleEmailUpdate(e)}
        />
        <Button
          data-testid="signup-button"
          loading={loading}
          onClick={handleSignup}
          style={{ backgroundColor: '#0076FF' }}
          disabled={error || email.length === 0}
          className={classes.inputBtn}
        >
          <NormalText>Sign up</NormalText>
        </Button>
        <ErrorLabel error={error} errorMessage={errorMessage} width="350px" />
      </Flex>
    </>
  );
};

export default SignupWithEmail;
